import { IconDots } from '@tabler/icons-react';
import Highlight from '@tiptap/extension-highlight';
import { JSONContent } from '@tiptap/react';
import { EnrollmentCommentDto } from '@zorro/clients';
import { getFullName } from '@zorro/common-utils';
import { ActionIcon } from '@zorro/design-system/v2/ActionIcon';
import { Menu } from '@zorro/design-system/v2/Menu';
import { Modal } from '@zorro/design-system/v2/Modal';
import { Tooltip } from '@zorro/design-system/v2/Tooltip';
import {
  formatDateTimeEnLocaleFromUtc,
  formatRelativeDateTime,
} from '@zorro/shared/formatters';
import {
  Box,
  Group,
  RichTextViewer,
  Stack,
  Text,
  useRichTextEditor,
} from '@zorro/web/design-system';
import { useImpersonation, useMonolithMutation } from '@zorro/web/ui-utils';
import { useState } from 'react';

import classNames from './Comment.module.scss';
import { CustomShortcuts } from './Comments';
import { DeleteCommentForm } from './DeleteCommentForm';
import { RichTextCommentEditor } from './RichTextCommentEditor';

type Props = EnrollmentCommentDto & {
  isSelected?: boolean;
  onCopyLink: (id: string) => Promise<string>;
};

export const Comment = ({
  performedByFirstName,
  performedByLastName,
  content,
  createdAt,
  updatedAt,
  id,
  isSelected,
  performedById,
  onCopyLink,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isDeleteCommentModalOpen, setIsDeleteCommentModalOpen] =
    useState(false);
  const { authUser } = useImpersonation();

  const [editedContent, setEditedContent] = useState<JSONContent>(
    JSON.parse(content)
  );

  const { tryMutate: updateComment } = useMonolithMutation({
    method: 'onboardingPeriodsControllerUpdateEnrollmentComment',
  });

  const { editor } = useRichTextEditor(
    {
      extensions: [Highlight, CustomShortcuts],
      content: editedContent,
      onUpdate({ editor }) {
        const content = editor.isEmpty ? {} : editor.getJSON();
        setEditedContent(content);
      },
    },
    [content]
  );

  return (
    <Stack
      w="100%"
      gap="8px"
      p={16}
      bd="md"
      className={classNames.comment}
      data-selected={isSelected || open}
    >
      <Group justify="space-between" align="center">
        <Group gap={8}>
          <Text fw={600} fz={16} isMasked>
            {getFullName({
              firstName: performedByFirstName,
              lastName: performedByLastName,
            })}
          </Text>

          {!isEdit && (
            <Tooltip
              label={formatDateTimeEnLocaleFromUtc(createdAt)}
              position="top"
            >
              <Text size="sm" c="zorroGray.4">
                {formatRelativeDateTime(createdAt)}
              </Text>
            </Tooltip>
          )}
        </Group>
        {!isEdit && (
          <Box className={classNames.menuWrapper}>
            <Menu opened={open} onChange={setOpen} size="sm">
              <Menu.Target>
                <ActionIcon size="md" isChecked={open}>
                  <IconDots size="1rem" />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => onCopyLink(id)}>Copy link</Menu.Item>
                {authUser?.id === performedById && (
                  <>
                    <Menu.Item onClick={() => setIsEdit(true)}>Edit</Menu.Item>
                    <Menu.Item
                      onClick={() => setIsDeleteCommentModalOpen(true)}
                    >
                      Delete
                    </Menu.Item>
                  </>
                )}
              </Menu.Dropdown>
            </Menu>
          </Box>
        )}
      </Group>

      {isEdit ? (
        <RichTextCommentEditor
          editor={editor}
          onClick={() => {
            if (!editor?.getText().trim()) return;
            updateComment([id, { content: JSON.stringify(editedContent) }]);
            setIsEdit(false);
          }}
          isEdit={isEdit}
          onCancel={() => setIsEdit(false)}
          content={JSON.stringify(editedContent)}
        />
      ) : (
        <RichTextViewer content={JSON.parse(content)} />
      )}

      {updatedAt && updatedAt !== createdAt && (
        <Tooltip
          label={formatDateTimeEnLocaleFromUtc(updatedAt)}
          position="top"
        >
          <Text size="xs" w="fit-content" c="zorroGray.4">
            (edited)
          </Text>
        </Tooltip>
      )}

      <Modal
        size="lg"
        onClose={() => setIsDeleteCommentModalOpen(false)}
        isOpen={isDeleteCommentModalOpen}
        title="Delete comment"
      >
        <DeleteCommentForm
          onClose={() => setIsDeleteCommentModalOpen(false)}
          id={id}
        />
      </Modal>
    </Stack>
  );
};
